// CFGIA.B1 (#1541) — shared GitHub PAT + default-org form.
//
// Extracted from the two copies that had diverged: GitHubInfraStep (wizard)
// used to hand-roll its own org/token inputs saved via
// POST /tenants/:id/infrastructure-config with NO live validation (a
// syntactically-plausible but wrong/expired token saved silently, only
// discoverable later via a separate "Test Connection" click), while
// GitHubConnectionCard's PAT-fallback saved via
// PATCH /tenants/:id/credentials/github, which live-validates against
// GitHub's API before persisting.
//
// #1541 decision (Path B, locked): canonicalize both surfaces on the
// validated PATCH /credentials/github path for the token — a bad token is
// now rejected with 400 before it ever lands, and the "Test Connection" step
// this replaces is gone because save IS the test. That endpoint auto-detects
// the org from the token's account and returns it as `orgName`; org itself
// remains an independent, always-editable field saved via
// POST /infrastructure-config (org-only) — the auto-detected value is a
// starting point, not a lock.
//
// Self-contained: resolves the current org + whether a token is already set
// via its own GET /infrastructure-config fetch on mount, and reports
// {githubOrg, githubTokenSet} up via onSaved both once that fetch resolves
// and after every successful save — so a host (wizard step or Settings card)
// never needs a duplicate fetch of its own to know the current state.
import { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../../lib/api';
import PasswordInput from '../PasswordInput';
import GithubPatPermissionsHelp from '../GithubPatPermissionsHelp';

export function isValidGithubPatFormat(token: string): boolean {
  return token.startsWith('ghp_') || token.startsWith('github_pat_');
}

export interface GithubPatOrgState {
  githubOrg: string;
  githubTokenSet: boolean;
}

interface ExistingConfig {
  githubOrg: string | null;
  githubTokenSet: boolean;
}

interface Props {
  tenantId: string;
  isAdmin: boolean;
  /** Seeds the org field before the mount fetch resolves (e.g. the wizard's locally-cached progress). */
  initialOrg?: string;
  /**
   * Fired once the initial fetch resolves ('initial'), after an org-only
   * save ('org-save'), and after a token save ('token-save'). These are
   * distinguished (not collapsed into one 'save') because a host that reacts
   * to a token actually landing (e.g. collapsing a disclosure + refetching
   * connection status) must NOT do so on an unrelated org-only save just
   * because a token already happened to be set beforehand — nor on the
   * passive 'initial' report of pre-existing state.
   */
  onSaved?: (data: GithubPatOrgState, source: 'initial' | 'org-save' | 'token-save') => void;
  /** Renders a Cancel button next to Save. Omit for an always-visible host (the wizard has no collapse-back-down affordance). */
  onCancel?: () => void;
}

const labelStyle: React.CSSProperties = {
  display: 'block', fontFamily: 'var(--font-body)', fontSize: 13,
  fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6,
  textTransform: 'uppercase', letterSpacing: '0.04em',
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)', background: 'var(--surface-alt)',
  color: 'var(--text)', fontFamily: 'var(--font-body)', fontSize: 14,
  outline: 'none', boxSizing: 'border-box',
};

const helperStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)', fontSize: 12,
  color: 'var(--text-secondary)', marginTop: 4,
};

const fieldGap: React.CSSProperties = { marginBottom: 20 };

const primaryBtnStyle = (disabled: boolean): React.CSSProperties => ({
  fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500,
  padding: '9px 14px', background: 'transparent',
  border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
  color: 'var(--text-secondary)', cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.6 : 1,
});

export default function GithubPatOrgForm({ tenantId, isAdmin, initialOrg, onSaved, onCancel }: Props) {
  const [githubOrg, setGithubOrg]     = useState(initialOrg ?? '');
  const [orgInitial, setOrgInitial]   = useState(initialOrg ?? '');
  const [tokenSet, setTokenSet]       = useState(false);
  const [githubToken, setGithubToken] = useState('');
  const [updateToken, setUpdateToken] = useState(false);
  const [orgSaving, setOrgSaving]     = useState(false);
  const [orgSaved, setOrgSaved]       = useState(false);
  const [orgError, setOrgError]       = useState<string | null>(null);
  const [tokenSaving, setTokenSaving] = useState(false);
  const [tokenError, setTokenError]   = useState<string | null>(null);
  const tokenInputRef = useRef<HTMLInputElement>(null);
  // Set the moment the user edits the org field, so the mount fetch below
  // (which can resolve after the user has already started typing) never
  // clobbers an in-progress edit — a ref (not state) so the async .then
  // reads the CURRENT value, not one captured at effect-setup time.
  const orgTouchedRef = useRef(false);
  // Set by a SUCCESSFUL org or token save. The mount fetch was issued at
  // component-mount time — strictly before any save action can occur — so
  // it can never be fresher than a save that has already landed by the time
  // it resolves. Without this guard, a slow mount fetch resolving AFTER a
  // fast save would silently revert tokenSet/org back to pre-save values and
  // re-fire onSaved(..., 'initial') with that stale data, corrupting a
  // host's tracked state even though the real save already succeeded.
  const hasSavedRef = useRef(false);
  // Kept in sync with their respective state so handleSaveOrg/handleSaveToken
  // can read the LATEST value of the field they didn't just save, rather
  // than a value closed over at call time — org-save and token-save are two
  // independent in-flight requests with no ordering guarantee between them,
  // so without this a slower save's resolution can report a stale value for
  // the other field (e.g. org-save resolving after a concurrent token-save
  // reporting githubTokenSet as whatever it was BEFORE that token-save).
  const tokenSetRef  = useRef(tokenSet);
  const githubOrgRef = useRef(githubOrg);
  useEffect(() => { tokenSetRef.current = tokenSet; }, [tokenSet]);
  useEffect(() => { githubOrgRef.current = githubOrg; }, [githubOrg]);

  useEffect(() => {
    apiFetch<ExistingConfig>(`/tenants/${tenantId}/infrastructure-config`)
      .then(cfg => {
        if (hasSavedRef.current) return; // a real save already superseded this fetch
        const org = cfg.githubOrg ?? initialOrg ?? '';
        const tokenAlreadySet = !!cfg.githubTokenSet;
        if (!orgTouchedRef.current) setGithubOrg(org);
        setOrgInitial(org);
        setTokenSet(tokenAlreadySet);
        onSaved?.({ githubOrg: org, githubTokenSet: tokenAlreadySet }, 'initial');
      })
      .catch(() => {
        if (hasSavedRef.current) return; // a real save already superseded this fetch
        // Even on failure, report SOMETHING so a host gating on the first
        // onSaved call (e.g. the wizard's "Continue" button) isn't left
        // permanently blocked by a one-off network failure with no retry
        // affordance at this gate — falls back to initialOrg/no-token-known,
        // the same assumption the pre-#1541 wizard silently made on a failed
        // fetch (existingConfig stayed null, tokenFieldActive defaulted true).
        onSaved?.({ githubOrg: initialOrg ?? '', githubTokenSet: false }, 'initial');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  // Poll briefly for browser/password-manager autofill, which doesn't always
  // dispatch onChange/onInput reliably. Mirrors GitHubInfraStep's pre-#1541
  // workaround for the same input — that original had the same stale-closure
  // shape fixed here: comparing against a ref kept in sync with githubToken
  // (rather than closing over githubToken directly, which the one-time
  // ([]-deps) effect below would otherwise freeze at its mount-time value)
  // so the poll only fires setTokenError(null) on a GENUINE external change,
  // not on every tick where the field happens to be non-empty.
  const githubTokenRef = useRef(githubToken);
  useEffect(() => { githubTokenRef.current = githubToken; }, [githubToken]);

  useEffect(() => {
    const el = tokenInputRef.current;
    if (!el) return;
    const check = () => {
      if (el.value && el.value !== githubTokenRef.current) {
        setGithubToken(el.value);
        setTokenError(null);
      }
    };
    const t1 = setTimeout(check, 500);
    const t2 = setTimeout(check, 1500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const tokenFieldActive = !tokenSet || updateToken;
  const patFormatInvalid = tokenFieldActive && githubToken.length > 0 && !isValidGithubPatFormat(githubToken);

  const handleSaveOrg = async () => {
    if (!isAdmin || orgSaving) return;
    const trimmed = githubOrg.trim();
    setOrgSaving(true);
    setOrgError(null);
    setOrgSaved(false);
    try {
      await apiFetch(`/tenants/${tenantId}/infrastructure-config`, {
        method: 'POST',
        body:   JSON.stringify({ githubOrg: trimmed }),
      });
      hasSavedRef.current = true;
      setGithubOrg(trimmed);
      setOrgInitial(trimmed);
      setOrgSaved(true);
      // Read via ref, not the closed-over tokenSet: a concurrent token-save
      // (independent in-flight request, no ordering guarantee against this
      // one) may have resolved and changed tokenSet WHILE this org-save's
      // request was in flight — reporting the ref's current value instead of
      // this call's stale snapshot means whichever save resolves LAST still
      // reports the true current tokenSet, not a stale one.
      onSaved?.({ githubOrg: trimmed, githubTokenSet: tokenSetRef.current }, 'org-save');
    } catch (e) {
      setOrgError(e instanceof Error ? e.message : 'Failed to save organisation');
    } finally {
      setOrgSaving(false);
    }
  };

  const handleSaveToken = async () => {
    if (!isAdmin || tokenSaving || !isValidGithubPatFormat(githubToken)) return;
    setTokenSaving(true);
    setTokenError(null);
    try {
      const result = await apiFetch<{ orgName?: string }>(`/tenants/${tenantId}/credentials/github`, {
        method: 'PATCH',
        body:   JSON.stringify({ token: githubToken }),
      });
      // Server auto-detects the org from the token's account (Path B) — a
      // starting point, not a lock; the org field above stays independently
      // editable via handleSaveOrg. Falls back to the ref (not the closed-
      // over githubOrg) for the same cross-save-race reason as tokenSetRef
      // above — in the rare case the server omits orgName.
      const detectedOrg = result.orgName ?? githubOrgRef.current;
      hasSavedRef.current = true;
      setTokenSet(true);
      setUpdateToken(false);
      setGithubToken('');
      setGithubOrg(detectedOrg);
      setOrgInitial(detectedOrg);
      onSaved?.({ githubOrg: detectedOrg, githubTokenSet: true }, 'token-save');
    } catch (e) {
      setTokenError(e instanceof Error ? e.message : 'Failed to save token');
    } finally {
      setTokenSaving(false);
    }
  };

  const handleCancelToken = () => {
    setGithubToken('');
    setTokenError(null);
    onCancel?.();
  };

  const focusRing = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.12)';
    e.currentTarget.style.borderColor = 'var(--primary)';
  };
  const blurRing = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.style.boxShadow = 'none';
    e.currentTarget.style.borderColor = 'var(--border)';
  };

  return (
    <div data-testid="github-pat-org-form">
      <div style={fieldGap}>
        <label style={labelStyle}>GitHub Organisation</label>
        <input
          type="text"
          value={githubOrg}
          onChange={e => { orgTouchedRef.current = true; setGithubOrg(e.target.value); setOrgSaved(false); setOrgError(null); }}
          disabled={!isAdmin || orgSaving}
          placeholder="my-org"
          style={{ ...inputStyle, opacity: (!isAdmin || orgSaving) ? 0.6 : 1 }}
          onFocus={focusRing}
          onBlur={blurRing}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          data-testid="github-org-input"
        />
        <p style={helperStyle}>Optional — required for repository provisioning</p>
        {orgError && (
          <p role="alert" style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--error)', marginTop: 4 }}>
            {orgError}
          </p>
        )}
        {orgSaved && !orgError && (
          <p
            style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--success)', marginTop: 4 }}
            data-testid="github-org-saved"
          >
            Saved.
          </p>
        )}
        <div style={{ marginTop: 10 }}>
          <button
            type="button"
            disabled={!isAdmin || orgSaving || githubOrg.trim() === orgInitial.trim()}
            onClick={() => void handleSaveOrg()}
            style={primaryBtnStyle(!isAdmin || orgSaving || githubOrg.trim() === orgInitial.trim())}
            data-testid="github-org-save"
          >
            {orgSaving ? 'Saving…' : 'Save org'}
          </button>
        </div>
      </div>

      <div style={fieldGap} data-testid="github-pat-form">
        <label style={labelStyle}>GitHub Personal Access Token</label>
        {tokenSet && !updateToken ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <input
              type="password"
              value="••••••••[saved]"
              disabled
              style={{ ...inputStyle, color: 'var(--text-secondary)', flex: 1 }}
            />
            <button
              type="button"
              onClick={() => setUpdateToken(true)}
              disabled={!isAdmin}
              style={{ ...primaryBtnStyle(!isAdmin), flexShrink: 0 }}
              data-testid="github-pat-update-toggle"
            >
              Update token
            </button>
          </div>
        ) : (
          <PasswordInput
            ref={tokenInputRef}
            value={githubToken}
            onChange={e => { setGithubToken(e.target.value); setTokenError(null); }}
            onInput={e => {
              const val = (e.currentTarget as HTMLInputElement).value;
              if (val !== githubToken) { setGithubToken(val); setTokenError(null); }
            }}
            placeholder="ghp_… or github_pat_…"
            style={inputStyle}
            onFocus={focusRing}
            onBlur={blurRing}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            aria-invalid={patFormatInvalid ? 'true' : 'false'}
            aria-describedby={'github-pat-help' + (patFormatInvalid ? ' github-pat-error' : '')}
            data-testid="github-pat-input"
          />
        )}

        {patFormatInvalid && (
          <p
            id="github-pat-error"
            role="alert"
            style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--error)', marginTop: 4 }}
          >
            Token must start with <code>ghp_</code> or <code>github_pat_</code>.
          </p>
        )}

        {tokenFieldActive && (
          <GithubPatPermissionsHelp id="github-pat-help" />
        )}

        {tokenError && (
          <p role="alert" style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--error)', marginTop: 6 }}>
            {tokenError}
          </p>
        )}

        <p style={{ ...helperStyle, marginTop: 6 }}>Never stored in plain text.</p>

        {tokenFieldActive && (
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button
              type="button"
              disabled={!isAdmin || tokenSaving || githubToken.length === 0 || !isValidGithubPatFormat(githubToken)}
              onClick={() => void handleSaveToken()}
              style={{
                ...primaryBtnStyle(!isAdmin || tokenSaving || githubToken.length === 0 || !isValidGithubPatFormat(githubToken)),
                background: 'var(--primary)', color: '#fff', border: 'none',
              }}
              data-testid="github-pat-save"
            >
              {tokenSaving ? 'Saving…' : 'Save token'}
            </button>
            {onCancel && (
              <button
                type="button"
                onClick={handleCancelToken}
                disabled={tokenSaving}
                style={primaryBtnStyle(tokenSaving)}
                data-testid="github-pat-cancel"
              >
                Cancel
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
